import Honeybadger from 'honeybadger-js';
import React, { useMemo, useState } from 'react';

import { useSendSaveTheDateEmailMutation } from '../../../generated/graphql';
import { getChangedValues } from '../../../helpers/getChangedValues';
import { splitHouseholds } from '../../../helpers/households';
import { editSaveTheDateEmailPath, editSaveTheDateEmailRoute } from '../../../helpers/routes';
import EmailSequence from '../../SharePage/EmailSequence';
import { emailSharingSchema } from '../../SharePage/sharingValidation';
import useShareTracking from '../../SharePage/useShareTracking';
import { EmailPreviewContainer } from '../EmailPreviewContainer';
import { useFilteredHouseholds } from '../GuestFilters/useFilteredHouseholds';
import EmailEditorRail from './ShareWebsiteEmail/EmailEditorRail';
import { useSequenceState } from './useSequenceState';

export interface ShareValues {
  subject: string;
  message: string;
  linkButtonText: string;
}

const SaveTheDateEmail: React.FC = () => {
  const [sendSaveTheDate, { loading: sendingMessage }] = useSendSaveTheDateEmailMutation();

  const {
    sequence,
    wwsData,
    wwsLoading,
    guestIds,
    setGuestIds,
    setHideRecaptcha,
    hideRecaptcha,
    dispatch,
    recaptchaRef,
    seqId,
    showError,
    errorMessage,
    showSuccess,
    householdLoading,
  } = useSequenceState();

  const { filteredHouseholds, setFilterCriteria } = useFilteredHouseholds();
  const households = useMemo(() => splitHouseholds('email', filteredHouseholds), [filteredHouseholds]);

  const loading = wwsLoading || sendingMessage;
  const track = useShareTracking();

  const message = sequence?.messagingSequence?.defaultEmailBody ?? '';

  let initialValues: ShareValues = {
    subject: sequence?.messagingSequence?.defaultEmailSubject ?? '',
    message: wwsData?.weddingWebsite.pinProtect
      ? `${message} Our website is private, so please enter the password ${wwsData.weddingWebsite.pin} to enter the site.`
      : message,
    linkButtonText: sequence?.messagingSequence?.defaultLinkText ?? '',
  };

  const [showLinkButton, setShowLinkButton] = useState(true);

  const sendMessage = async (values: ShareValues) => {
    if (loading) {
      return;
    }

    const edited = getChangedValues(initialValues, values);

    //Save the date message sent tracking
    track('Save the Date Interaction', {
      action: 'Save the Date template sent',
      guestMessagedCount: guestIds.length,
      message: values.message,
      messageCustomized: Boolean(Object.keys(edited).length),
      messageType: sequence?.messagingSequence?.displayName,
      medium: 'email',
    });

    dispatch({ type: 'RESET', payload: { hideRecaptcha: true, showSuccess: false, showError: false } });

    try {
      // Convert all newlines in the message body into <br /> tags.
      const convertedMessage = values.message.replace(/\n/g, '<br/>');
      await sendSaveTheDate({
        variables: {
          args: {
            subject: values.subject,
            body: convertedMessage,
            linkButtonText: values.linkButtonText,
            guestIds: guestIds,
            token: recaptchaRef.current?.getValue() || '',
            showLinkButton: wwsData?.weddingWebsite?.state === 'published' && showLinkButton,
            // Now everyone gets themedEmail
            // TODO: Remove this after it's removed from GMS
            themeEmail: true,
          },
        },
      });
      dispatch({ type: 'SUCCESS' });
    } catch (error) {
      Honeybadger.notify(error, 'Error sending share website email', { context: { error } });
      dispatch({ type: 'ERROR' });
    } finally {
      recaptchaRef.current?.reset();
    }
  };

  const editRoute = editSaveTheDateEmailRoute(seqId);

  return (
    <EmailSequence
      loading={loading}
      showError={showError}
      errorMessage={errorMessage}
      showSuccess={showSuccess}
      initialValues={initialValues}
      onSend={sendMessage}
      PreviewComponent={
        <EmailPreviewContainer
          editRoute={editRoute}
          matchPath={editSaveTheDateEmailPath}
          displayName={sequence?.messagingSequence?.displayName}
          showLinkButton={showLinkButton}
        />
      }
      EditorComponent={
        <EmailEditorRail
          matchPath={editSaveTheDateEmailPath}
          noLinkButton={wwsData?.weddingWebsite?.state !== 'published'}
          showLinkButton={showLinkButton}
          setShowLinkButton={setShowLinkButton}
          initialValues={initialValues}
        />
      }
      hideRecaptcha={hideRecaptcha}
      setHideRecaptcha={setHideRecaptcha}
      recaptchaRef={recaptchaRef}
      setGuestIds={setGuestIds}
      editPath={editSaveTheDateEmailPath}
      editRoute={editRoute}
      validationSchema={emailSharingSchema}
      households={households}
      householdLoading={householdLoading}
      setFilter={setFilterCriteria}
      displayName={sequence?.messagingSequence?.displayName}
    />
  );
};

export default SaveTheDateEmail;
